import { EXAMPLE_IDS } from './example-ids.js';
import { EXAMPLE_ANSWER_IDS, EXAMPLE_NOW } from './examples.js';
const ids = EXAMPLE_IDS;
const OLD_SEF_LINE = 'Old Sef swears the lamplighters walked into the tide the night the bells fell silent.';
/** The palette's answer for "lamp": Missing Lamplighters by its title; no SRD hit. */
export const EXAMPLE_PALETTE_RESULT = {
    canon: [
        {
            id: ids.questMissingLamplighters.id,
            fid: 'LanternQuest0001',
            title: 'Missing Lamplighters',
            titlePending: false,
            typeKey: 'quest',
            library: 'canon',
            isStub: false,
            canonStatus: 'canon',
            matched: 'title',
            alias: null,
            snippet: null,
        },
    ],
    reference: { hits: [], total: 0 },
};
/** Old Sef named in plain text in Missing Lamplighters (its first name in that block). */
export const EXAMPLE_UNLINKED = {
    sourceDocId: ids.questMissingLamplighters.id,
    sourceFid: 'LanternQuest0001',
    sourceTitle: 'Missing Lamplighters',
    sourceTitlePending: false,
    sourceTypeKey: 'quest',
    sourceBodyVersion: 1,
    blockId: ids.blockMissingLamplightersOldSef.id,
    ordinal: 0,
    text: 'Old Sef',
    name: 'Old Sef',
    isAlias: false,
    context: OLD_SEF_LINE,
    inSecret: false,
    inGmNote: false,
};
/** The Deity's custom template. */
export const EXAMPLE_TEMPLATE = {
    id: ids.templateDeityCustom.id,
    docTypeId: ids.typeDeity.id,
    name: 'Deity',
    variant: 'custom',
    body: {
        type: 'doc',
        content: [
            { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Worship' }] },
            { type: 'paragraph' },
        ],
    },
    bodySchemaVersion: 1,
    props: {},
    links: [],
    sortKey: 'a0',
    archived: false,
};
/** The template `templates.create`'s example makes. */
export const EXAMPLE_TEMPLATE_CREATED = {
    ...EXAMPLE_TEMPLATE,
    id: EXAMPLE_ANSWER_IDS.templateCreated,
    name: 'Minor deity',
    variant: 'quick',
    body: { type: 'doc', content: [{ type: 'paragraph' }] },
    sortKey: 'a1',
};
/** A template write the rows refuse (1.S's `TemplateInvalid`): `details.issue` names it. */
const templateInvalid = (code, path) => ({
    status: 422,
    body: {
        code: 'invalid_body',
        message: 'The template cannot be stored.',
        details: { issue: { code, path } },
    },
});
const unsupportedVersion = {
    status: 422,
    body: {
        code: 'unsupported_schema_version',
        message: 'The template body is of a schema version the server cannot read.',
    },
};
/**
 * The answers CS r2c declared on the template routes (1.S deliverable 9 item 1; DECISIONS 141 item
 * 8), one example each, with `template_type_mismatch` beside them. Phase 1 templates hold no links,
 * so a non-empty `links` is refused (DECISIONS 141 item 1). The sentences are examples: a client
 * reads `code` and `details`, never `message`.
 */
export const EXAMPLE_TEMPLATE_REFUSALS = {
    'templates.create': [
        {
            ...templateInvalid('links_unsupported', ['links']),
            request: {
                body: {
                    docTypeId: ids.typeDeity.id,
                    name: 'Sea deity',
                    variant: 'custom',
                    body: { type: 'doc', content: [{ type: 'paragraph' }] },
                    links: [{ linkTypeKey: 'related_to', targetId: ids.npcMaelisDorn.id }],
                },
            },
        },
        {
            ...unsupportedVersion,
            request: {
                body: {
                    docTypeId: ids.typeDeity.id,
                    name: 'Sea deity',
                    variant: 'custom',
                    body: { type: 'doc', content: [{ type: 'paragraph' }] },
                    bodySchemaVersion: 99,
                },
            },
        },
    ],
    'templates.update': [
        // The Deity's only demo template is the custom one, so no demo row meets a taken name.
        templateInvalid('name_taken', ['name']),
        {
            ...unsupportedVersion,
            request: { params: { id: ids.templateDeityCustom.id }, body: { bodySchemaVersion: 99 } },
        },
    ],
    'docs.applyTemplate': [
        {
            status: 409,
            body: {
                code: 'version_conflict',
                message: 'The document changed since this edit began.',
                details: { current: { bodyVersion: 1, metaVersion: 1 }, conflictRevNo: null },
            },
            request: {
                params: { id: ids.deityDrownedQueen.id },
                body: { templateId: ids.templateDeityCustom.id, baseBodyVersion: 7 },
            },
        },
        {
            status: 409,
            body: { code: 'document_trashed', message: 'The document is in the trash.' },
        },
        templateInvalid('invalid_template_body', ['body', 'content', 0]),
        {
            status: 422,
            body: {
                code: 'template_type_mismatch',
                message: 'The template is for another type.',
            },
        },
    ],
};
/** The pin of Salt in the Wells. */
export const EXAMPLE_SIDEBAR_PIN = {
    id: ids.sidebarPinSaltInTheWells.id,
    parentId: null,
    kind: 'document',
    refId: ids.questSaltInTheWells.id,
    label: null,
    sortKey: 'a0',
};
/** The group "Session prep". */
export const EXAMPLE_SIDEBAR_GROUP = {
    id: ids.sidebarGroupPrep.id,
    parentId: null,
    kind: 'group',
    refId: null,
    label: 'Session prep',
    sortKey: 'a1',
};
/** Maelis Dorn pinned into "Session prep" by `sidebar.add`'s example. */
export const EXAMPLE_SIDEBAR_ADDED = {
    id: EXAMPLE_ANSWER_IDS.sidebarItemCreated,
    parentId: ids.sidebarGroupPrep.id,
    kind: 'document',
    refId: ids.npcMaelisDorn.id,
    label: null,
    sortKey: 'a0',
};
/** Missing Lamplighters for the table-read: its one paragraph, nothing hidden in it. */
export const EXAMPLE_RENDERED = {
    target: 'table',
    html: `<p>${OLD_SEF_LINE}</p>`,
    warnings: [],
};
/** A Markdown export, as `exports.create` answers it: queued. */
export const EXAMPLE_EXPORT = {
    id: ids.exportMarkdown.id,
    format: 'markdown',
    status: 'queued',
    requestedAt: EXAMPLE_NOW,
    finishedAt: null,
    assetId: null,
    bytes: null,
    unrenderedPages: null,
};
//# sourceMappingURL=examples-workspace.js.map